import { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Badge,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  CircularProgress,
  Divider,
  Tooltip,
  alpha,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";

import useNotifications from "@/hooks/useNotifications";
import ClickOutside from "./click-outside";
import useNotificationStore from "@/hooks/useNotificationStore";
import { useTheme } from "@mui/material/styles";
import { useSession } from "@/hooks/useSession";

// Helper function to get user ID consistently
const getUserId = (session) => {
  try {
    return session?.id || 
      session?.user_id || 
      JSON.parse(localStorage.getItem("user_Info") || "{}")?.id ||
      "guest";
  } catch {
    return "guest";
  }
};

const DropdownNotification = () => {
  const theme = useTheme();
  const { session } = useSession();
  const [anchorEl, setAnchorEl] = useState(null);
  const [isNavigating, setIsNavigating] = useState(false);
  
  // Store clicked notifications per user in localStorage
  const [clickedNotifications, setClickedNotifications] = useState(() => {
    try {
      const userId = getUserId(session);
      const stored = localStorage.getItem(`clickedNotifications_${userId}`);
      return new Set(JSON.parse(stored || "[]"));
    } catch {
      return new Set();
    }
  });
  
  const iconButtonRef = useRef(null);
  const open = Boolean(anchorEl);
  const navigate = useNavigate();
  const { setSelectedWorkPermitId } = useNotificationStore();
  const hasBookmarkedOnOpen = useRef(false);

  const {
    notifications,
    unreadCount,
    notificationsLoading,
    bookmarkNotification,
  } = useNotifications();

  // Persist clicked notifications to localStorage per user
  useEffect(() => {
    try {
      const userId = getUserId(session);
      localStorage.setItem(
        `clickedNotifications_${userId}`,
        JSON.stringify([...clickedNotifications])
      );
    } catch (error) {
      console.error("Failed to persist clicked notifications:", error);
    }
  }, [clickedNotifications, session]);

  // When dropdown opens, bookmark the latest notification
  useEffect(() => {
    if (open && notifications.length > 0 && !hasBookmarkedOnOpen.current) {
      hasBookmarkedOnOpen.current = true;
      const latestNotification = notifications[0]; // First item is the newest
      if (latestNotification) {
        bookmarkNotification(latestNotification.id).catch((error) => {
          console.error("Failed to bookmark latest notification:", error);
        });
      }
    }
  }, [open, notifications, bookmarkNotification]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    hasBookmarkedOnOpen.current = false;
  };

  // Mark notification as clicked (read) in localStorage
  const handleMarkAsClicked = useCallback(
    (notificationId) => {
      setClickedNotifications((prev) => new Set(prev).add(notificationId));
    },
    []
  );

  // Mark all notifications as read (clicked)
  const handleMarkAllAsRead = useCallback(() => {
    const allNotificationIds = notifications.map((n) => n.id);
    setClickedNotifications(new Set(allNotificationIds));
  }, [notifications]);

  // Separate function to handle navigation
  const handleNavigation = useCallback(
    (notification) => {
      const workPermitId = notification.metadata?.WORK_PERMIT_ID;

      if (workPermitId) {
        setSelectedWorkPermitId(workPermitId);
        navigate("/docs", { state: { workPermitId } });
      }
    },
    [setSelectedWorkPermitId, navigate]
  );

  // Main notification click handler - separated concerns
  const handleNotificationClick = useCallback(
    (notification) => {
      if (isNavigating) return;

      setIsNavigating(true);

      try {
        const notificationId = notification.id;

        // 1. Mark as clicked in localStorage
        handleMarkAsClicked(notificationId);

        // 2. Navigate to document
        handleNavigation(notification);
      } catch (error) {
        console.error("Error processing notification:", error);
      } finally {
        // 3. Close dropdown and reset navigation state
        handleClose();
        setTimeout(() => setIsNavigating(false), 300);
      }
    },
    [isNavigating, handleMarkAsClicked, handleNavigation]
  );

  return (
    <ClickOutside onClick={handleClose} exceptionRef={iconButtonRef}>
      <Tooltip title="Notifications">
        <IconButton
          sx={{ color: "primary.contrastText" }}
          onClick={handleClick}
          ref={iconButtonRef}
          aria-label={`Show ${unreadCount} new notifications`}
        >
          <Badge badgeContent={unreadCount} color="error">
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        sx={{
          "& .MuiPaper-root": {
            width: 360,
            maxHeight: 400,
            borderRadius: 2,
            boxShadow: theme.shadows[8],
          },
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem disabled>
          <Typography variant="subtitle1" fontWeight="bold">
            Notifications ({unreadCount})
          </Typography>
        </MenuItem>
        <Divider />

        {notificationsLoading ? (
          <MenuItem>
            <Box display="flex" justifyContent="center" width="100%">
              <CircularProgress size={24} />
            </Box>
          </MenuItem>
        ) : notifications.length > 0 ? (
          notifications.map((notification) => {
            const isClicked = clickedNotifications.has(notification.id);

            return (
              <MenuItem
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                disabled={isNavigating}
                sx={{
                  backgroundColor: !isClicked
                    ? alpha(theme.palette.primary.main, 0.08)
                    : "inherit",
                  borderLeft: !isClicked
                    ? `4px solid ${theme.palette.primary.main}`
                    : "4px solid transparent",
                  transition: theme.transitions.create(
                    ["background-color", "border-left"],
                    { duration: theme.transitions.duration.short }
                  ),
                  "&:hover": {
                    backgroundColor: !isClicked
                      ? alpha(theme.palette.primary.main, 0.12)
                      : alpha(theme.palette.action.hover, 0.04),
                  },
                }}
                role="menuitem"
                aria-label={`${notification.message}${isClicked ? ", read" : ", unread"}`}
              >
                <Box display="flex" alignItems="center" width="100%">
                  <Box flexGrow={1}>
                    <Typography
                      variant="body2"
                      fontWeight={isClicked ? "normal" : "bold"}
                      sx={{
                        color: isClicked
                          ? theme.palette.text.secondary
                          : theme.palette.text.primary,
                      }}
                    >
                      {notification.message}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {new Date(notification.created_at).toLocaleString()}
                    </Typography>
                  </Box>

                  {/* Visual indicator for unread status */}
                  {!isClicked && (
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: "primary.main",
                        ml: 1,
                      }}
                    />
                  )}
                </Box>
              </MenuItem>
            );
          })
        ) : (
          <MenuItem disabled>
            <Typography variant="body2" color="textSecondary">
              No notifications
            </Typography>
          </MenuItem>
        )}

        {/* Mark all as read button at the bottom */}
        {notifications.length > 0 && (
          <>
            <Divider />
            <MenuItem
              onClick={handleMarkAllAsRead}
              disabled={isNavigating}
              sx={{
                justifyContent: "center",
                color: theme.palette.primary.main,
                fontWeight: 500,
                py: 1.5,
                "&:hover": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                },
              }}
            >
              <Typography variant="body2" fontWeight={500}>
                Mark all as read
              </Typography>
            </MenuItem>
          </>
        )}
      </Menu>
    </ClickOutside>
  );
};

export default DropdownNotification;
